// Durable, per-user store for "the discount banner is temporarily collapsed
// until this timestamp". Unlike the permanent dismissal (see
// `discountDismissal.ts`), collapsing hides the banner for a short window and
// then lets it re-appear — the minus control, versus the X dismiss.
//
// Kept in its own tiny localStorage key (not the shared redux-persist blob) for
// the same reason the dismissal is: on mobile WebViews the aggregate blob can
// hit the storage quota and be silently dropped. A dedicated key stays small
// and survives independently.
//
// Keyed by user id so a collapse on a shared device never leaks to a different
// account, mirroring the per-user keys the banner already uses.

export const DISCOUNT_COLLAPSE_DURATION_MS = 60 * 60 * 1000; // 1 hour

const KEY_PREFIX = 'ora-discount-collapsed';
const keyFor = (userId: string): string => `${KEY_PREFIX}:${userId}`;

// Returns the epoch-ms timestamp the banner is collapsed until, or null when
// there is no (valid) collapse marker. Expired markers are treated as null.
export const readDiscountCollapsedUntil = (
  userId: string | null | undefined
): number | null => {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const until = Number(raw);
    return Number.isFinite(until) ? until : null;
  } catch {
    // Storage unavailable (e.g. private mode). Best-effort only.
    return null;
  }
};

export const writeDiscountCollapsedUntil = (
  userId: string | null | undefined,
  untilMs: number
): void => {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(keyFor(userId), String(untilMs));
  } catch {
    // Storage unavailable or over quota. The in-memory state still hides the
    // banner for the current session; durability across reloads is best-effort.
  }
};
